import { QUESTION_STEPS } from '@/components/OnboardingQuestionPage'
import { getAnswer } from '@/utils/onboardingStorage'
import ContinueButton from '@app-studio-web/ui/components/ContinueButton'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useLocation, useNavigate } from 'react-router-dom'

const TIME_OPTIONS = [
  {
    label: 'pages.onboarding.repetitiveness.most',
    dots: 4,
    weight: 4,
    value: 'most',
  },
  {
    label: 'pages.onboarding.repetitiveness.goodChunk',
    dots: 3,
    weight: 3,
    value: 'good-chunk',
  },
  {
    label: 'pages.onboarding.repetitiveness.some',
    dots: 2,
    weight: 2,
    value: 'some',
  },
  {
    label: 'pages.onboarding.repetitiveness.aLittle',
    dots: 1,
    weight: 1,
    value: 'a-little',
  },
]

const SEGMENT_COLORS = [
  'bg-accent',
  'bg-blue-500',
  'bg-accent',
  'bg-blue-500',
  'bg-accent',
  'bg-blue-500',
  'bg-accent',
  'bg-blue-500',
  'bg-accent',
]

export default function RepetitivenessPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const currentPath = location.pathname.split('/').pop() || ''
  const currentIndex = QUESTION_STEPS.indexOf(currentPath)
  const stepNumber = currentIndex + 1
  const totalSteps = QUESTION_STEPS.length
  const progress = totalSteps > 0 ? (stepNumber / totalSteps) * 100 : 0

  const selectedTasks = useMemo(() => {
    const taskAnswer = getAnswer('daily-tasks')
    if (!taskAnswer) return []
    return taskAnswer.answer.split(', ').filter(Boolean)
  }, [])

  const taskLabels: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {}
    selectedTasks.forEach((val) => {
      map[val] = val
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    })
    return map
  }, [selectedTasks])

  const [allocations, setAllocations] = useState<Record<string, string>>({})

  const allAssigned =
    selectedTasks.length > 0 && selectedTasks.every((task) => allocations[task])

  const segments = useMemo(() => {
    const totalWeight = selectedTasks.reduce((sum, task) => {
      const alloc = allocations[task]
      const opt = TIME_OPTIONS.find((o) => o.value === alloc)
      return sum + (opt?.weight || 0)
    }, 0)
    if (totalWeight === 0) return []

    return selectedTasks
      .filter((task) => allocations[task])
      .map((task, i) => {
        const opt = TIME_OPTIONS.find((o) => o.value === allocations[task])
        const pct = Math.round(((opt?.weight || 0) / totalWeight) * 100)
        return { task, pct, color: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }
      })
  }, [allocations, selectedTasks])

  if (selectedTasks.length === 0) {
    return (
      <div className="bg-app-bg flex min-h-dvh flex-col items-center justify-center px-6">
        <p className="text-muted">
          {t('pages.onboarding.repetitiveness.goBack', {
            defaultValue: 'No tasks selected. Please go back.',
          })}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-accent mt-4 cursor-pointer underline"
        >
          {t('shared.back', { defaultValue: 'Go back' })}
        </button>
      </div>
    )
  }

  return (
    <div className="bg-app-bg text-primary flex min-h-dvh flex-col font-sans">
      <div className="bg-accent/30 fixed top-0 left-0 z-10 h-full w-1">
        <div
          className="bg-accent w-full transition-all duration-500"
          style={{ height: `${progress}%` }}
        />
      </div>

      <div className="bg-border h-1 w-full">
        <div
          className="bg-accent h-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="text-muted hover:text-primary flex cursor-pointer items-center gap-1 bg-transparent text-sm transition-colors"
        >
          ← {t('shared.back', { defaultValue: 'Back' })}
        </button>
        <span className="text-muted text-sm">
          {stepNumber} / {totalSteps}
        </span>
      </div>

      <div className="mx-auto w-full max-w-2xl grow px-6 pt-10 pb-28">
        <span className="text-accent text-xs font-semibold tracking-widest uppercase">
          STEP {stepNumber}
        </span>

        <h1 className="mt-3 font-serif text-[1.75rem] leading-tight font-bold tracking-tight md:text-[2.25rem]">
          {t('pages.onboarding.repetitiveness.title', {
            defaultValue: 'How do you split your time?',
          })}
        </h1>

        <p className="text-muted mt-4 text-base leading-relaxed">
          {t('pages.onboarding.repetitiveness.subtitle', {
            defaultValue:
              "For each task, pick how much of your week it takes up. We'll do the math.",
          })}
        </p>

        <Form method="post" id="time-split-form" className="mt-10 space-y-8">
          {selectedTasks.map((taskValue) => (
            <div key={taskValue}>
              <h3 className="mb-3 text-base font-semibold">
                {taskLabels[taskValue]}
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {TIME_OPTIONS.map((opt) => {
                  const selected = allocations[taskValue] === opt.value
                  return (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border px-3 py-3 text-center transition-colors ${
                        selected
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-accent/30 bg-white'
                      }`}
                    >
                      <input
                        className="hidden"
                        type="radio"
                        name={`time-${taskValue}`}
                        value={opt.value}
                        onChange={() =>
                          setAllocations((prev) => ({
                            ...prev,
                            [taskValue]: opt.value,
                          }))
                        }
                      />
                      <span className="flex gap-0.5">
                        {Array.from({ length: opt.dots }).map((_, i) => (
                          <span
                            key={i}
                            className={`inline-block h-2 w-2 rounded-full ${
                              selected ? 'bg-accent' : 'bg-[#aaa]'
                            }`}
                          />
                        ))}
                      </span>
                      <span
                        className={`text-xs ${selected ? 'text-accent font-medium' : 'text-muted'}`}
                      >
                        {t(opt.label, {
                          defaultValue:
                            opt.value === 'most'
                              ? 'Most of my time'
                              : opt.value === 'good-chunk'
                                ? 'A good chunk'
                                : opt.value === 'some'
                                  ? 'Some time'
                                  : 'A little',
                        })}
                      </span>
                    </label>
                  )
                })}
              </div>
              <div className="border-border mt-3 border-b" />
            </div>
          ))}

          <input
            type="hidden"
            name={t('pages.onboarding.repetitiveness.title', {
              lng: 'en',
              defaultValue: 'How do you split your time?',
            })}
            value={JSON.stringify(allocations)}
          />

          {segments.length > 0 && (
            <div className="mt-4">
              <div className="flex h-4 w-full overflow-hidden rounded-full">
                {segments.map((seg) => (
                  <div
                    key={seg.task}
                    className={`${seg.color} transition-all duration-300`}
                    style={{ width: `${seg.pct}%` }}
                  />
                ))}
              </div>
              <div className="mt-1.5 flex">
                {segments.map((seg) => (
                  <span
                    key={seg.task}
                    className="text-muted text-xs"
                    style={{ width: `${seg.pct}%` }}
                  >
                    {seg.pct}%
                  </span>
                ))}
              </div>
            </div>
          )}

          <div
            className={`border-border bg-app-bg fixed bottom-0 left-0 z-20 w-full border-t px-6 py-4 transition-all duration-200 ${
              allAssigned
                ? 'translate-y-0 opacity-100'
                : 'translate-y-full opacity-0'
            }`}
          >
            <div className="mx-auto max-w-2xl">
              <ContinueButton disabled={!allAssigned} />
            </div>
          </div>
        </Form>
      </div>
    </div>
  )
}
